import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Err, Ok } from 'ts-results'

import { useNotifications } from 'app/application/application'
import { useApplicationStore } from 'app/application/infrastructure'
import { DataError, useApi } from 'app/common/application'
import { useConnections } from 'app/connections/application'

import {
  Audience,
  AudienceConfiguration,
  AudienceCreateRequest,
  AudienceCreateResponse,
  AudiencesGetRequest,
  AudiencesGetResponse,
  CreateAudience,
  FacebookCreateAudience,
  GoogleAnalyticsCreateAudience,
  TikTokCreateAudience,
  mapToAudiences,
} from '../types'
import { AudienceCreateRequestContent, CreateAudienceBase } from '../types/createAudience'

const getData = (
  create: (
    endpoint: string,
    data: AudiencesGetRequest,
  ) => Promise<Err<DataError<number>> | Ok<AudiencesGetResponse>>,
  id?: string,
): Promise<Err<DataError<number>> | Ok<AudiencesGetResponse>> =>
  create('/signals/query', {
    content: id
      ? { id, type: 'audience' }
      : {
          type: 'audience',
        },
  })

const isValidConfiguration = (audienceConfiguration: AudienceConfiguration) => {
  if (!audienceConfiguration.name || !audienceConfiguration.type) {
    return false
  }

  switch (audienceConfiguration.connection?.name) {
    case 'facebook':
      return !!audienceConfiguration.adAccountId && !!audienceConfiguration.pixelId
    case 'googleAnalytics':
      return !!audienceConfiguration.audienceKey
    case 'tiktok':
      return !!audienceConfiguration.adAccountId
    default:
      return false
  }
}

const mapConfiguration = (
  audienceConfiguration: Required<AudienceConfiguration>,
): CreateAudience => {
  const audience: CreateAudienceBase = {
    name: audienceConfiguration.name,
    type: audienceConfiguration.type,
  }

  switch (audienceConfiguration.connection.name) {
    case 'facebook': {
      const facebookAudience: FacebookCreateAudience = {
        ...audience,
        config: {
          adAccountId: audienceConfiguration.adAccountId,
          metaPixelId: audienceConfiguration.pixelId,
        },
      }

      return facebookAudience
    }
    case 'googleAnalytics': {
      const googleAnalytics: GoogleAnalyticsCreateAudience = {
        ...audience,
        config: {
          targetingKey: audienceConfiguration.audienceKey,
        },
      }

      return googleAnalytics
    }
    case 'tiktok': {
      const tiktokAudience: TikTokCreateAudience = {
        ...audience,
        advertiserId: audienceConfiguration.adAccountId,
      }

      return tiktokAudience
    }
  }
}

const useAudiences = () => {
  const { create } = useApi()
  const { connections } = useConnections()
  const { createNotification } = useNotifications()
  const { t } = useTranslation('error')

  const audienceData = useApplicationStore((state) => state.audiences)
  const addAudienceData = useApplicationStore((state) => state.addAudience)
  const setAudienceData = useApplicationStore((state) => state.setAudiences)
  const [audiences, setAudiences] = useState<Audience[]>([])

  useEffect(
    () => setAudiences(mapToAudiences(audienceData, connections)),
    [audienceData, connections],
  )

  const createAudience = useCallback(
    async (audienceConfiguration: AudienceConfiguration) => {
      if (isValidConfiguration(audienceConfiguration)) {
        const result = await create<AudienceCreateRequest, AudienceCreateResponse>(
          '/signals/create',
          {
            content: {
              audience: mapConfiguration(audienceConfiguration as Required<AudienceConfiguration>),
              connectionId: audienceConfiguration.connection!.id,
              treatmentIds: audienceConfiguration.treatments.map((treatment) => treatment.id),
            } satisfies AudienceCreateRequestContent,
          },
        )
        if (result.ok) {
          addAudienceData(result.val.data)

          return Ok(true)
        } else {
          createNotification({
            message: result.val.message,
            title: t(`http.${result.val.code}`),
            type: 'error',
          })

          return Err(result.val)
        }
      }

      return Ok(false)
    },
    [addAudienceData, create, createNotification, t],
  )

  const fetchAudiences = useCallback(async () => {
    const result = await getData(create)
    if (result.ok) {
      setAudienceData(result.val.data)
    } else {
      createNotification({
        message: result.val.message,
        title: t(`http.${result.val.code}`),
        type: 'error',
      })
    }

    return Promise.resolve()
  }, [create, createNotification, setAudienceData, t])

  const getAudience = useCallback(
    async (id: string): Promise<Audience | null> => {
      if (!audiences.length) {
        return null
      }

      let audience = audiences.find((audience) => audience.id === id) ?? null
      if (!audience && connections.length) {
        audience =
          (
            await getData(create, id).then((result) =>
              result.ok ? mapToAudiences(result.val.data, connections) : [],
            )
          ).find((audience) => audience.id === id) ?? null
      }

      return audience
    },
    [audiences, create, connections],
  )

  return {
    audiences,
    createAudience,
    fetchAudiences,
    getAudience,
    isValidConfiguration,
  }
}

export default useAudiences
